import { forwardRef, InputHTMLAttributes } from 'react';
import classNames from 'classnames';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  variant?: 'default' | 'error' | 'success';
  inputSize?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  leftAddon?: React.ReactNode;
  rightAddon?: React.ReactNode;
  helperText?: string;
  error?: string;
}

/**
 * Input component - Custom implementation replacing Flowbite
 *
 * Features:
 * - Multiple variants (default, error, success)
 * - Multiple sizes (sm, md, lg)
 * - Left and right addons (icons, text, buttons)
 * - Helper text and error messages
 * - Full accessibility support
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'default',
      inputSize = 'md',
      fullWidth = false,
      leftAddon,
      rightAddon,
      helperText,
      error,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const actualVariant = error ? 'error' : variant;

    const baseStyles =
      'block border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantStyles = {
      default:
        'border-input bg-input text-foreground focus:border-ring focus:ring-ring',
      error:
        'border-error-500 bg-error-50 dark:bg-error-900/10 text-foreground focus:border-error-600 focus:ring-error-500',
      success:
        'border-success-500 bg-success-50 dark:bg-success-900/10 text-foreground focus:border-success-600 focus:ring-success-500',
    };

    const sizeStyles = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4 text-base',
      lg: 'h-12 px-5 text-lg',
    };

    const inputClasses = classNames(
      baseStyles,
      variantStyles[actualVariant],
      sizeStyles[inputSize],
      fullWidth && 'w-full',
      leftAddon && 'pl-10',
      rightAddon && 'pr-10',
      className
    );

    const containerClasses = classNames('relative', fullWidth && 'w-full');

    const addonClasses =
      'absolute top-1/2 -translate-y-1/2 flex items-center text-neutral-500';

    return (
      <div className={containerClasses}>
        {leftAddon && (
          <div className={classNames(addonClasses, 'left-3')}>{leftAddon}</div>
        )}
        <input
          ref={ref}
          className={inputClasses}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={
            error
              ? `${props.id}-error`
              : helperText
                ? `${props.id}-helper`
                : undefined
          }
          {...props}
        />
        {rightAddon && (
          <div className={classNames(addonClasses, 'right-3')}>
            {rightAddon}
          </div>
        )}
        {error && (
          <p
            id={`${props.id}-error`}
            className="mt-1.5 text-sm text-error-600 dark:text-error-400"
          >
            {error}
          </p>
        )}
        {!error && helperText && (
          <p
            id={`${props.id}-helper`}
            className="mt-1.5 text-sm text-muted-foreground"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
